import { type ContractReviewMessage } from "../../amplify/backend/function/OfferExpCheckout/ts/public/types";

const CONTRACT_REVIEW_CATEGORIES = [
  "customer_name_check",
  "home_type_check",
  "nem_agreement_check",
  "utility_bill_check",
  "estimation_method_check",
] as const;

type ContractReviewCategory = (typeof CONTRACT_REVIEW_CATEGORIES)[number];

const CONTRACT_REVIEW_CATEGORY_SET = new Set<string>(
  CONTRACT_REVIEW_CATEGORIES
);

const isKnownContractReviewCategory = (
  category: string
): category is ContractReviewCategory =>
  CONTRACT_REVIEW_CATEGORY_SET.has(category);

const filterKnownContractReviewMessages = (
  messages: ContractReviewMessage[] | undefined
): ContractReviewMessage[] =>
  (messages ?? []).filter((m) => isKnownContractReviewCategory(m.category));

const getMessagesByCategory = (
  messages: ContractReviewMessage[],
  ...categories: ContractReviewCategory[]
): ContractReviewMessage[] =>
  (messages ?? []).filter((m) =>
    categories.includes(m.category as ContractReviewCategory)
  );

const isMobileOrManufacturedHomeMessage = (
  msg: ContractReviewMessage
): boolean =>
  msg.category === "home_type_check" &&
  /mobile|manufactured/i.test(msg.message);

const getMessagesForStep = (
  messages: ContractReviewMessage[],
  stepId: string
): ContractReviewMessage[] => {
  let categories: ContractReviewCategory[] = [];

  if (stepId === "confirmNames") {
    categories = ["nem_agreement_check"];

    if (!hasMessage(messages, isTrustAgreementNameMatchMessage)) {
      categories.push("customer_name_check");
    }

    return getMessagesByCategory(messages, ...categories);
  }

  if (stepId === "linkUtilityAccount") {
    categories = ["utility_bill_check"];

    if (hasMessage(messages, isTrustAgreementNameMatchMessage)) {
      categories.push("customer_name_check");
    }

    if (hasMessage(messages, isMobileOrManufacturedHomeMessage)) {
      categories.push("home_type_check");
    }

    if (hasMessage(messages, is12MonthActualUsageMethodMessage)) {
      categories.push("estimation_method_check");
    }

    return getMessagesByCategory(messages, ...categories);
  }

  return [];
};

const hasMessagesForStep = (
  messages: ContractReviewMessage[],
  stepId: string
): boolean => getMessagesForStep(messages, stepId).length > 0;

const isSameContractReviewMessage = (
  a: ContractReviewMessage,
  b: ContractReviewMessage
): boolean =>
  a.category === b.category &&
  a.message === b.message &&
  (a.suggestion ?? "") === (b.suggestion ?? "");

const getContractReviewMessageDismissKey = (
  message: ContractReviewMessage
): string =>
  JSON.stringify([message.category, message.message, message.suggestion ?? ""]);

/** First checkout task (sidebar order) that shows the first message in API order, then next message, etc. */
const getFirstTaskIdForOrderedContractReviewMessages = (
  messages: ContractReviewMessage[],
  tasks: { id: string }[]
): string | undefined => {
  const stepMsgsByTask = tasks.map((task) => ({
    id: task.id,
    stepMsgs: getMessagesForStep(messages, task.id),
  }));

  for (const msg of messages) {
    for (const { id, stepMsgs } of stepMsgsByTask) {
      if (stepMsgs.some((m) => isSameContractReviewMessage(m, msg))) {
        return id;
      }
    }
  }
  return stepMsgsByTask.find((t) => t.stepMsgs.length > 0)?.id;
};

const isRateScheduleMismatchMessage = (msg: ContractReviewMessage): boolean =>
  msg.category === "utility_bill_check" &&
  /Rate schedule mismatch/i.test(msg.message);

const isTrustAgreementNameMatchMessage = (
  msg: ContractReviewMessage
): boolean =>
  msg.category === "customer_name_check" &&
  /Trust ownership detected/i.test(msg.message);

const isNemAgreementCheckMessage = (msg: ContractReviewMessage): boolean =>
  msg.category === "nem_agreement_check" &&
  /Customer name on utility bill/i.test(msg.message);

const is12MonthActualUsageMethodMessage = (
  msg: ContractReviewMessage
): boolean =>
  msg.category === "estimation_method_check" &&
  /Square Footage but 12 months of actual usage/i.test(msg.message);

const hasMessage = (
  messages: ContractReviewMessage[],
  filterCondition: (message: ContractReviewMessage) => boolean
): boolean => messages.some(filterCondition);

export {
  filterKnownContractReviewMessages,
  getMessagesByCategory,
  getContractReviewMessageDismissKey,
  getFirstTaskIdForOrderedContractReviewMessages,
  getMessagesForStep,
  hasMessagesForStep,
  isKnownContractReviewCategory,
  isMobileOrManufacturedHomeMessage,
  isRateScheduleMismatchMessage,
  isTrustAgreementNameMatchMessage,
  is12MonthActualUsageMethodMessage,
  hasMessage,
  isNemAgreementCheckMessage,
};
export type { ContractReviewCategory };
